import type {
  ChargeItem,
  Encounter,
  Extension,
  Identifier,
  Period,
  Reference,
} from "@medplum/fhirtypes";

import { applyChargeItemStatusTag } from "@/billing/components/ServicesTab/utils/charge-item-status-tag";
import type { BuildChargeItemPayloadInput } from "@/billing/types/custom-service-charge-item";
import { buildChargeItemNoteExtensions } from "@/billing/utils/charge-item-notes";
import { getProcedureFeeChargeItemDefinitionUrl } from "@/fhir/charge-item-definition";
import { Extensions } from "@/fhir/enums/extensions";
import { paymentMethodExtension } from "@/utils/billing";
import { getCodeableConceptCode, joinReferenceString } from "@/utils/medplum";

/** Units a charge starts at when the caller does not pass a quantity. */
export const DEFAULT_CHARGE_ITEM_PAYLOAD_QUANTITY = 1;

export type { BuildChargeItemPayloadInput };

/**
 * One repeatable ChargeItem modifier extension per non-blank string.
 *
 * Uses {@link Extensions.ChargeItemProcedureModifiers} so a single charge can carry zero or more
 * modifiers without a multi-valued extension entry.
 */
export function buildChargeItemModifierExtensions(modifiers: string[] | undefined): Extension[] {
  if (!modifiers?.length) return [];

  return modifiers
    .map((modifier) => modifier.trim())
    .filter(Boolean)
    .map((valueString) => ({
      url: Extensions.ChargeItemProcedureModifiers,
      valueString,
    }));
}

const toContextField = (
  context: Reference<Encounter> | undefined,
): Pick<ChargeItem, "context"> | Record<string, never> => (context ? { context } : {});

const toOccurrencePeriodField = (
  occurrencePeriod: Period | undefined,
): Pick<ChargeItem, "occurrencePeriod"> | Record<string, never> =>
  occurrencePeriod ? { occurrencePeriod } : {};

const toIdentifierField = (
  identifier: Identifier[] | undefined,
): Pick<ChargeItem, "identifier"> | Record<string, never> =>
  identifier?.length ? { identifier } : {};

const toSupportingInformationField = (
  supportingInformation: Reference[] | undefined,
): Pick<ChargeItem, "supportingInformation"> | Record<string, never> =>
  supportingInformation?.length ? { supportingInformation } : {};

const toDefinitionCanonicalField = (
  procedureCode: string,
  practiceOrganizationId: string | undefined,
): Pick<ChargeItem, "definitionCanonical"> | Record<string, never> => {
  if (!procedureCode) return {};

  return {
    definitionCanonical: [
      getProcedureFeeChargeItemDefinitionUrl(procedureCode, practiceOrganizationId),
    ],
  };
};

const toPerformerField = (
  performerPractitionerId: string | undefined,
): Pick<ChargeItem, "performer"> | Record<string, never> => {
  if (!performerPractitionerId) return {};

  return {
    performer: [
      {
        actor: {
          reference: joinReferenceString("Practitioner", performerPractitionerId),
        },
      },
    ],
  };
};

const buildPlaceOfServiceExtension = (value: string): Extension => ({
  url: Extensions.ChargeItemPlaceOfService,
  valueString: value,
});

const toPlaceOfServiceExtension = (placeOfService: string | undefined): Extension[] => {
  const value = placeOfService?.trim();
  return value ? [buildPlaceOfServiceExtension(value)] : [];
};

/**
 * Reads a ChargeItem's place-of-service extension.
 *
 * Returns the trimmed value, or undefined when the charge carries no extension or a blank one.
 * Does not validate against the current place-of-service value set — callers that need a
 * narrowed, current-option-list value do that validation themselves.
 */
export function resolveChargeItemPlaceOfService(chargeItem: ChargeItem): string | undefined {
  const value = chargeItem.extension?.find(
    (extension) => extension.url === Extensions.ChargeItemPlaceOfService,
  )?.valueString;

  return value?.trim() || undefined;
}

/**
 * Replaces a ChargeItem's place-of-service extension with a new value.
 *
 * Every other extension is kept as-is; only the place-of-service entry is dropped and re-added, so
 * a charge never carries more than one at a time.
 */
export function replacePlaceOfServiceExtension(
  extensions: Extension[] | undefined,
  placeOfService: string,
): Extension[] {
  const kept = (extensions ?? []).filter(
    (extension) => extension.url !== Extensions.ChargeItemPlaceOfService,
  );

  return [...kept, buildPlaceOfServiceExtension(placeOfService)];
}

/**
 * Builds the billing ChargeItem payload used by custom services and encounter capture.
 *
 * Pure: no Medplum I/O. Omits empty optional fields so callers can pass only what they have.
 * Always stamps the service-payer extension. Procedure fee definitionCanonical is derived from
 * the code's first coding when one exists. Notes are stamped only when non-blank, so a charge with
 * neither carries no note extension. Practice compartment linkage runs after create via
 * `$qh-set-accounts`, once the ChargeItem has an id.
 */
export function buildChargeItemPayload(input: BuildChargeItemPayloadInput): ChargeItem {
  const procedureCode = getCodeableConceptCode(input.code);
  const quantity = input.quantity ?? DEFAULT_CHARGE_ITEM_PAYLOAD_QUANTITY;
  const modifierExtensions = buildChargeItemModifierExtensions(input.modifiers);
  const placeOfServiceExtensions = toPlaceOfServiceExtension(input.placeOfService);
  const noteExtensions = buildChargeItemNoteExtensions({
    billerNotes: input.billerNotes,
    providerNotes: input.providerNotes,
  });

  return applyChargeItemStatusTag({
    resourceType: "ChargeItem",
    status: input.status,
    code: input.code,
    subject: input.subject,
    ...toContextField(input.context),
    ...toOccurrencePeriodField(input.occurrencePeriod),
    ...toIdentifierField(input.identifier),
    ...toSupportingInformationField(input.supportingInformation),
    ...toDefinitionCanonicalField(procedureCode, input.practiceOrganizationId),
    quantity: { value: quantity },
    ...toPerformerField(input.performerPractitionerId),
    extension: [
      paymentMethodExtension(input.paymentMethod),
      ...modifierExtensions,
      ...placeOfServiceExtensions,
      ...noteExtensions,
    ],
  });
}
